import { once } from 'lodash'
import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { logger } from 'shared/lib'
import modalFullScreenStore from 'shared/ui/ModalFullScreen/store/modalFullScreenStore'
import { ZIndex } from 'shared/constants/zIndex'
import {
  Chatbot,
  ChatbotApi,
  ChatbotInstructionType,
  type IChatbotInstructionTemplate,
} from 'entities/Chatbot'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { cacheStore } from 'entities/Cache/CacheStore'
import { ChatbotTemplateModal } from 'pages/chatbot/ui/ChatbotCreate'
import { type IAgentType } from 'pages/chatbot/store/type'
import { UnifiedCreateModal } from 'pages/chatbot/ui/ChatbotCreate/UnifiedCreateModal'
import { CreateAgentModal } from 'pages/chatbot/ui/CreateAgentModal/CreateAgentModal'

export class CreateChatbotStore {
  private _templateModalId = 'createChatbotTemplateModal'
  private _instructions = new Map<ChatbotInstructionType, string>()
  get instructions() {
    return this._instructions
  }

  readonly chatbot

  constructor(private _entities: IEntitiesStore, readonly type: IAgentType) {
    this.chatbot = new Chatbot({ entities: this._entities })
    makeAutoObservable(this)
  }

  init = once(async () => {
    try {
      let instructions = cacheStore.getData<IChatbotInstructionTemplate[]>('getInstructions')
      if (!instructions) {
        const {
          data: { templates },
        } = await ChatbotApi.getInstructions()
        cacheStore.setData('getInstructions', templates)
        instructions = templates
      }
      if (instructions) {
        instructions.forEach(({ type, instruction }) => this._instructions.set(type, instruction))
      }
    } catch (error) {
      logger.error(error)
    }
  })

  createAgentModal = (type: string) => {
    const id = nanoid()
    const onClose = () => modalFullScreenStore.removeModal(id)

    modalFullScreenStore.addModal({
      id,
      zIndex: ZIndex.BASE_99,
      maxWidth: '620px',
      ModalContent: CreateAgentModal,
      ModalContentProps: {
        store: this,
        type,
        onClose,
      },
      onClose,
    })
  }

  create = async () => {
    const isUnifiedAgent = this._entities.featureFlagsStore.unified_agent_enabled

    if (isUnifiedAgent) {
      modalStore.addModal({
        id: this._templateModalId,
        title: 'Choose type',
        pureContent: true,
        ModalContent: () => <UnifiedCreateModal store={this} />,
        width: 480,
      })
    } else {
      await this.init()
      if (!this._entities.featureFlagsStore.salesmsg_ai_agent_book_appointment_v2) {
        const instruction = this._instructions.get(this.chatbot.type as ChatbotInstructionType)
        if (instruction) this.chatbot.settings.setInstruction(instruction)
        else {
          this.chatbot.setType(ChatbotInstructionType.LeadQualification)
        }
      }

      modalStore.addModal({
        id: this._templateModalId,
        title: 'Choose template',
        pureContent: true,
        ModalContent: () => <ChatbotTemplateModal store={this} />,
        width: 798,
      })
    }
  }

  onClose = () => {
    modalStore.removeModal(this._templateModalId)
  }
}
